import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Navbar } from '../../components/Navbar';
import { LoadingSpinner, Pagination } from '../../components/common';
import Footer from '../../components/Footer';
import { ReauthBanner } from '../../components/ReauthBanner';
import { useAppContext } from '../../contexts/useAppContext';
import { Repository } from '../../types';
import api from '../../services/authenticatedApi';
import { analytics } from '../../services/analytics/posthog';
import { logger } from '../../utils/logger';
import { ProviderTabs } from './ProviderTabs';
import { ConnectedHeader, ErrorBanner } from './ConnectedHeader';
import { RepositoryFilters } from './RepositoryFilters';
import { RepositoryList } from './RepositoryList';
import { OrgAccessBanners } from './OrgAccessBanners';
import { ConnectPrompt } from './ConnectPrompt';
import {
  buildAvailableOrganizations,
  collectAvailableLanguages,
  filterAndSortRepositories,
} from './derivations';
import { useRepositoryData } from './useRepositoryData';
import { ProviderTab } from './types';

export const RepositorySelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { setSelectedRepository } = useAppContext();
  const data = useRepositoryData();

  const {
    user,
    isAuthenticated,
    authLoading,
    activeTab,
    searchQuery,
    repositories,
    connections,
    providerConnections,
    activeConnection,
    loading,
    loadingRepos,
    syncing,
    error,
    errorStack,
    totalRepos,
    reauthConnections,
    orgsData,
    connectingProvider,
    selectedOrganization,
    selectedVisibility,
    selectedLanguage,
    selectedScanStatus,
    sortBy,
    sortOrder,
    currentPage,
    itemsPerPage,
    needsConnection,
    tokenInitialized,
    setSelectedOrganization,
    setSelectedVisibility,
    setSelectedLanguage,
    setSelectedScanStatus,
    setSortBy,
    setSortOrder,
    setCurrentPage,
    setError,
    setErrorStack,
    setReauthConnections,
    setConnectingProvider,
    updateUrlParams,
    handleConnectionSelect,
    loadConnections,
    loadRepositories,
    handleSync,
  } = data;

  const availableOrganizations = useMemo(
    () => buildAvailableOrganizations(orgsData, repositories),
    [orgsData, repositories],
  );

  const availableLanguages = useMemo(() => collectAvailableLanguages(repositories), [repositories]);

  const filteredRepositories = useMemo(
    () =>
      filterAndSortRepositories(repositories, {
        selectedOrganization,
        selectedVisibility,
        selectedLanguage,
        selectedScanStatus,
        sortBy,
        sortOrder,
      }),
    [
      repositories,
      selectedOrganization,
      selectedVisibility,
      selectedLanguage,
      selectedScanStatus,
      sortBy,
      sortOrder,
    ],
  );

  const paginatedRepositories = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRepositories.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRepositories, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredRepositories.length / itemsPerPage);

  useEffect(() => {
    if (
      !loading &&
      !authLoading &&
      isAuthenticated &&
      connections.length === 0 &&
      tokenInitialized
    ) {
      navigate('/settings?tab=repository');
    }
  }, [loading, authLoading, isAuthenticated, connections.length, tokenInitialized, navigate]);

  const handleSelectRepository = (repo: Repository) => {
    setSelectedRepository(repo);
    analytics.repositorySelected(repo.name, repo.platform as 'github' | 'gitlab' | 'local');
    navigate(`/repository/${repo.id}`, { state: { repository: repo } });
  };

  const handleConnect = async (provider: ProviderTab) => {
    try {
      if (!user) {
        setError('Please sign in first');
        return;
      }

      setConnectingProvider(provider);
      setError(null);

      sessionStorage.setItem('oauth_redirect_url', window.location.href);

      const response = await api.post(`/oauth/${provider}/initiate`);

      if (response.data && response.data.authorizationUrl) {
        window.location.href = response.data.authorizationUrl;
      } else {
        setConnectingProvider(null);
        setError('Failed to initiate OAuth flow');
      }
    } catch (err) {
      logger.error(`[OAuth] handleConnect: Failed for ${provider}`, { error: err });
      const errorMessage = err instanceof Error ? err.message : `Failed to connect ${provider}`;
      setError(errorMessage);
      setErrorStack(err instanceof Error ? err.stack || null : null);
      setConnectingProvider(null);
    }
  };

  const handleReauthorize = async (provider: string) => {
    try {
      if (!user) {
        setError('Please sign in first');
        return;
      }

      sessionStorage.setItem('oauth_redirect_url', window.location.href);

      const response = await api.post(`/oauth/${provider}/initiate?purpose=both`);

      if (response.data && response.data.authorizationUrl) {
        window.location.href = response.data.authorizationUrl;
      } else {
        setError('Failed to initiate reauthorization');
      }
    } catch (err) {
      logger.error(`[OAuth] handleReauthorize: Failed for ${provider}`, { error: err });
      const errorMessage = err instanceof Error ? err.message : `Failed to reauthorize ${provider}`;
      setError(errorMessage);
    }
  };

  const handleClearAllFilters = () => {
    updateUrlParams({ q: '' });
    setSelectedOrganization('');
    setSelectedVisibility('');
    setSelectedLanguage('');
    setSelectedScanStatus('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const pageTitle = connections.length === 0 ? 'Connect Git Provider' : 'Select Repository';

  return (
    <div className="min-h-screen flex flex-col bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 sm:px-6 lg:px-8">
      <Navbar pageTitle={pageTitle} showDivider={true} />

      <main className="flex-1 max-w-7xl mx-auto py-8 pt-24 w-full">
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Dashboard
          </button>
        </div>

        <ProviderTabs
          activeTab={activeTab}
          connections={connections}
          onSelect={(provider) => updateUrlParams({ provider })}
        />

        <div className="space-y-6">
          {!needsConnection && activeConnection ? (
            <>
              {error && (
                <ErrorBanner
                  error={error}
                  errorStack={errorStack}
                  onRetry={() => {
                    loadConnections();
                    loadRepositories();
                  }}
                  onDismiss={() => {
                    setError(null);
                    setErrorStack(null);
                  }}
                />
              )}

              {reauthConnections.length > 0 && (
                <ReauthBanner
                  connections={reauthConnections}
                  onReauthorize={handleReauthorize}
                  onDismiss={() => setReauthConnections([])}
                />
              )}

              <ConnectedHeader
                activeConnection={activeConnection}
                providerConnections={providerConnections}
                totalRepos={totalRepos}
                syncing={syncing}
                activeTab={activeTab}
                onConnectionSelect={handleConnectionSelect}
                onSync={handleSync}
              />

              <RepositoryFilters
                searchQuery={searchQuery}
                onSearchChange={(q) => updateUrlParams({ q })}
                availableOrganizations={availableOrganizations}
                selectedOrganization={selectedOrganization}
                setSelectedOrganization={setSelectedOrganization}
                availableLanguages={availableLanguages}
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                selectedVisibility={selectedVisibility}
                setSelectedVisibility={setSelectedVisibility}
                selectedScanStatus={selectedScanStatus}
                setSelectedScanStatus={setSelectedScanStatus}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                filteredCount={filteredRepositories.length}
                totalCount={repositories.length}
                onClearAll={handleClearAllFilters}
              />

              <OrgAccessBanners orgsData={orgsData} activeTab={activeTab} />

              <RepositoryList
                loadingRepos={loadingRepos}
                repositories={repositories}
                filteredRepositories={filteredRepositories}
                paginatedRepositories={paginatedRepositories}
                searchQuery={searchQuery}
                activeTab={activeTab}
                onSelectRepository={handleSelectRepository}
              />

              {totalPages > 1 && (
                <div className="mt-8">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredRepositories.length}
                    itemsPerPage={itemsPerPage}
                    hasNextPage={currentPage < totalPages}
                    hasPreviousPage={currentPage > 1}
                    onNextPage={() => {
                      if (currentPage < totalPages) {
                        setCurrentPage(currentPage + 1);
                      }
                    }}
                    onPreviousPage={() => {
                      if (currentPage > 1) {
                        setCurrentPage(currentPage - 1);
                      }
                    }}
                  />
                </div>
              )}
            </>
          ) : (
            <ConnectPrompt
              activeTab={activeTab}
              activeConnection={activeConnection}
              connectingProvider={connectingProvider}
              error={error}
              onConnect={handleConnect}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
